import React, { useEffect, useMemo, useRef, useState } from 'react'

import { classList } from 'react-classlist-helper'
import {
  arrayMove,
  SortableContainer,
  SortableElement,
} from 'react-sortable-hoc'

import { Button } from '~/client/src/shared/components/Button/Button'
import FacetButton from '~/client/src/shared/components/Filters/components/FacetButton/FacetButton'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { normalizePropertyName } from '../../propertyName'
import PropertyAccordion from '../ui/PropertyAccordion/PropertyAccordion'
import SelectOptionItem from '../ui/SelectOptionItem/SelectOptionItem'

interface SelectOption {
  value: string
  label: string
  deleted?: boolean
  isNew?: boolean
}

interface ISelectPropertyConfigProps {
  configuration: Record<string, any>
  onConfigChange: (configUpdater: (prev: any) => any) => void
}

const SortableItem = SortableElement(
  ({
    option,
    optionIndex,
    isEditing,
    showDragHandle,
    onStartEdit,
    onEdit,
    onCancelEdit,
    onDelete,
  }: {
    option: SelectOption
    optionIndex: number
    isEditing: boolean
    showDragHandle: boolean
    onStartEdit: (index: number) => void
    onEdit: (index: number, newLabel: string) => void
    onCancelEdit: () => void
    onDelete: (index: number) => void
  }) => {
    return (
      <div className="tw:z-[1] tw:select-none">
        <SelectOptionItem
          option={option}
          optionIndex={optionIndex}
          isEditing={isEditing}
          showDragHandle={showDragHandle}
          onStartEdit={onStartEdit}
          onEdit={onEdit}
          onCancelEdit={onCancelEdit}
          onDelete={onDelete}
        />
      </div>
    )
  },
)

const SortableList = SortableContainer(
  ({
    items,
    editingIndex,
    showDragHandle,
    onStartEdit,
    onEdit,
    onCancelEdit,
    onDelete,
  }: {
    items: SelectOption[]
    editingIndex: number | null
    showDragHandle: boolean
    onStartEdit: (index: number) => void
    onEdit: (index: number, newLabel: string) => void
    onCancelEdit: () => void
    onDelete: (index: number) => void
  }) => (
    <div className="tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-2">
      {items
        .map((option, index) => ({ option, originalIndex: index }))
        .filter(({ option }) => !option.deleted)
        .map(({ option, originalIndex }, visualIndex) => (
          <SortableItem
            key={`option-${originalIndex}`}
            index={visualIndex}
            optionIndex={originalIndex}
            option={option}
            isEditing={editingIndex === originalIndex}
            showDragHandle={showDragHandle}
            onStartEdit={onStartEdit}
            onEdit={onEdit}
            onCancelEdit={onCancelEdit}
            onDelete={onDelete}
          />
        ))}
    </div>
  ),
)

const SelectPropertyConfig: React.FC<ISelectPropertyConfigProps> = ({
  configuration: propertyConfiguration,
  onConfigChange,
}) => {
  type SortMode = 'name-asc' | 'name-desc' | 'custom'

  const [options, setOptions] = useState<SelectOption[]>([])
  const [allowUserCreation, setAllowUserCreation] = useState(false)
  const [includeNoneOption, setIncludeNoneOption] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [sortMode, setSortMode] = useState<SortMode>('custom')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const isLocalChangeRef = useRef(false)

  const visibleCount = useMemo(
    () => options.filter(opt => !opt.deleted).length,
    [options],
  )

  const updateOptions = (newOptions: SelectOption[]) => {
    setOptions(newOptions)
    isLocalChangeRef.current = true

    const processedOptions = newOptions.map(({ value, label, deleted }) => ({
      value,
      label,
      ...(deleted && { deleted }),
    }))

    onConfigChange(prev => ({
      ...prev,
      options: processedOptions,
    }))
  }

  useEffect(() => {
    if (isLocalChangeRef.current) {
      isLocalChangeRef.current = false
      return
    }

    if (!propertyConfiguration?.options) {
      setOptions([])
      setAllowUserCreation(!!propertyConfiguration?.allowCustomOptions)
      setIncludeNoneOption(!!propertyConfiguration?.includeNoneOption)
      return
    }

    const selectOptions: SelectOption[] = []

    try {
      const configOptions = Array.isArray(propertyConfiguration.options)
        ? propertyConfiguration.options
        : JSON.parse(propertyConfiguration.options as string)

      for (const option of configOptions) {
        if (typeof option === 'string') {
          selectOptions.push({ value: option, label: option })
        } else if (option?.value && option?.label) {
          selectOptions.push({
            value: option.value,
            label: option.label,
            ...(option.deleted && { deleted: option.deleted }),
          })
        }
      }
    } catch (error) {
      console.warn('Failed to parse select options:', error)
    }

    setOptions(selectOptions)
    setAllowUserCreation(!!propertyConfiguration.allowCustomOptions)
    setIncludeNoneOption(!!propertyConfiguration.includeNoneOption)
  }, [propertyConfiguration])

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    if (oldIndex === newIndex) return

    const visibleOptions = options.filter(opt => !opt.deleted)
    const reorderedVisible = arrayMove(visibleOptions, oldIndex, newIndex)
    const deletedOptions = options.filter(opt => opt.deleted)
    updateOptions([...reorderedVisible, ...deletedOptions])
    setSortMode('custom')
  }

  const handleStartEdit = (index: number) => {
    setEditingIndex(index)
  }

  const handleEditOption = (index: number, newLabel: string) => {
    const trimmedLabel = newLabel.trim()

    if (!trimmedLabel) {
      return
    }

    const option = options[index]
    const newValue = option.isNew
      ? normalizePropertyName(trimmedLabel)
      : trimmedLabel

    const isDuplicateValue = options.some(
      (opt, idx) => idx !== index && opt.value === newValue && !opt.deleted,
    )

    if (isDuplicateValue) {
      return
    }

    const updatedOptions = [...options]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isNew, ...restOption } = updatedOptions[index]
    updatedOptions[index] = {
      ...restOption,
      label: trimmedLabel,
      value: newValue,
    }
    updateOptions(updatedOptions)
    setEditingIndex(null)
  }

  const handleCancelEdit = () => {
    if (editingIndex !== null) {
      const option = options[editingIndex]
      if (option?.isNew) {
        const updatedOptions = options.filter((_, idx) => idx !== editingIndex)
        updateOptions(updatedOptions)
      }
    }
    setEditingIndex(null)
  }

  const handleDeleteOption = (index: number) => {
    const option = options[index]

    if (option.isNew) {
      const updatedOptions = options.filter((_, idx) => idx !== index)
      updateOptions(updatedOptions)
      if (editingIndex === index) {
        setEditingIndex(null)
      } else if (editingIndex !== null && editingIndex > index) {
        setEditingIndex(editingIndex - 1)
      }
    } else {
      const updatedOptions = [...options]
      updatedOptions[index] = {
        ...updatedOptions[index],
        deleted: true,
      }
      updateOptions(updatedOptions)
      if (editingIndex === index) {
        setEditingIndex(null)
      }
    }
  }

  const handleAddOption = () => {
    let newOptionName = Localization.translator.newOption
    let counter = 1

    while (options.some(opt => opt.label === newOptionName && !opt.deleted)) {
      counter++
      newOptionName = `${Localization.translator.newOption} ${counter}`
    }

    const newOption: SelectOption = {
      value: newOptionName,
      label: newOptionName,
      isNew: true,
    }

    const newOptions = [...options, newOption]
    updateOptions(newOptions)
    setEditingIndex(newOptions.length - 1)
  }

  const handleAllowUserCreationToggle = (checked: boolean) => {
    setAllowUserCreation(checked)
    isLocalChangeRef.current = true
    onConfigChange(config => ({
      ...config,
      allowCustomOptions: checked,
    }))
  }

  const handleIncludeNoneOptionToggle = (checked: boolean) => {
    setIncludeNoneOption(checked)
    isLocalChangeRef.current = true
    onConfigChange(config => ({
      ...config,
      includeNoneOption: checked,
    }))
  }

  const toggleSortDropdown = () => {
    setIsDropdownOpen(prev => !prev)
  }

  const applySort = (mode: SortMode) => {
    const visibleOptions = options.filter(opt => !opt.deleted)
    const deletedOptions = options.filter(opt => opt.deleted)

    const sortedVisible = [...visibleOptions]
    if (mode === 'name-asc') {
      sortedVisible.sort((a, b) => a.label.localeCompare(b.label))
    } else if (mode === 'name-desc') {
      sortedVisible.sort((a, b) => b.label.localeCompare(a.label))
    }

    updateOptions([...sortedVisible, ...deletedOptions])
  }

  const handleNameSort = () => {
    let newMode: SortMode
    if (sortMode === 'name-asc') {
      newMode = 'name-desc'
    } else if (sortMode === 'name-desc') {
      newMode = 'name-asc'
    } else {
      newMode = 'name-asc'
    }

    setSortMode(newMode)
    applySort(newMode)
    setIsDropdownOpen(false)
  }

  const handleCustomSort = () => {
    setSortMode('custom')
    setIsDropdownOpen(false)
  }

  return (
    <PropertyAccordion
      title={Localization.translator.dropdownOptionsSettings}
      isDefaultExpanded
    >
      <div className="tw:relative" ref={contentRef}>
        <div className="tw:flex tw:items-center tw:px-4 tw:py-2 tw:mb-2">
          <div className="tw:text-body-md tw:text-foreground">
            {Localization.translator.xOptionsAdded(visibleCount)}
          </div>
          {visibleCount > 1 && (
            <div className="tw:relative tw:inline-block tw:grow-0 tw:ml-auto">
              <FacetButton
                type="sort"
                label={`${
                  sortMode !== 'custom'
                    ? (sortMode === 'name-asc' ? '↑' : '↓') + ' '
                    : ''
                }${Localization.translator.sortBy}: ${
                  sortMode === 'custom'
                    ? Localization.translator.custom
                    : Localization.translator.name
                }`}
                isOpen={isDropdownOpen}
                isActive={sortMode !== 'custom'}
                onClick={toggleSortDropdown}
              />
              {isDropdownOpen && (
                <div className="tw:absolute tw:top-full tw:right-0 tw:z-[1001] tw:min-w-[180px] tw:bg-popover tw:border tw:border-solid tw:border-border tw:rounded tw:shadow-[0_14px_28px_rgba(0,0,0,0.25),0_10px_10px_rgba(0,0,0,0.25)] tw:mt-0.5">
                  <div className="tw:py-1">
                    <div
                      className={classList({
                        'tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-2 tw:text-body-md tw:cursor-pointer hover:tw:bg-muted': true,
                        'tw:bg-primary/10 tw:font-medium':
                          sortMode.startsWith('name-'),
                      })}
                      onClick={handleNameSort}
                    >
                      <span>{Localization.translator.name}</span>
                      {sortMode.startsWith('name-') && (
                        <span className="tw:ml-1 tw:text-muted-foreground tw:text-body-lg tw:font-bold">
                          {sortMode === 'name-asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                    <div
                      className={classList({
                        'tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-2 tw:text-body-md tw:cursor-pointer hover:tw:bg-muted': true,
                        'tw:bg-primary/10 tw:font-medium':
                          sortMode === 'custom',
                      })}
                      onClick={handleCustomSort}
                    >
                      <span>{Localization.translator.custom}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <SortableList
          items={options}
          editingIndex={editingIndex}
          showDragHandle={sortMode === 'custom'}
          onSortEnd={handleSortEnd}
          onStartEdit={handleStartEdit}
          onEdit={handleEditOption}
          onCancelEdit={handleCancelEdit}
          onDelete={handleDeleteOption}
          useDragHandle={true}
          lockAxis="y"
          helperContainer={() => contentRef.current || document.body}
        />
        <div className="tw:px-6 tw:py-4 tw:flex tw:flex-col tw:items-start tw:gap-4">
          <Button variant="outline" onClick={handleAddOption}>
            <AddIcon
              data-icon="inline-start"
              size="sm"
              className="tw:shrink-0"
            />
            {Localization.translator.addOption}
          </Button>
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:text-body-md tw:text-foreground">
            <span>{Localization.translator.allowUsersToCreateNewOptions}</span>
            <Switch
              checked={allowUserCreation}
              onCheckedChange={handleAllowUserCreationToggle}
            />
          </div>
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:text-body-md tw:text-foreground">
            <span>{Localization.translator.allowNoneSelection}</span>
            <Switch
              checked={includeNoneOption}
              onCheckedChange={handleIncludeNoneOptionToggle}
            />
          </div>
        </div>
      </div>
    </PropertyAccordion>
  )
}

export default SelectPropertyConfig
